<template>
  <section>
    <!-- Blocks the page while the file is uploading, so a second drop can't start a competing import. -->
    <z-overlay v-if="isUploading" notice="uploading" custom-style="position: fixed;" @cancel="cancelUpload" />
    <div class="row mt-2">
      <div class="col">
        <p>
          Use this facility to import the category/vehicle matrix <b>LINK</b> data. To kick off the import, drag and drop your comma-delimited file
          onto the grey area below.
        </p>
        <p><u>REMEMBER:</u> this only imports <b>LINKs</b>. Your category/vehicle matrix <u>should already be configured</u>.</p>
        <p>
          The file should have the following columns populated: Make, Model, Year, AdasCategory, System, Location, LinkType, LinkGroup, Links,
          Exceptions (in this order).
          <a href="https://opusadas.blob.core.windows.net/samples/ADAS%20MAP%20Vehicle%20Make%20Matrix%20Links%20SAMPLE.csv">Sample File Here</a>
        </p>
        <p>NOTE: the LinkType must be a pipe-delimited ( | ) string containing at least one of the following: System, Location, Static, Dynamic.</p>
        <p>NOTE: Exceptions must be a string defined as pipe-delimited ( | ) for rows and tilde-delimited ( ~ ) for each row.</p>
      </div>
    </div>
    <div class="row mt-2">
      <div class="col">
        <file-pond
          ref="pond"
          name="test"
          :server="{ process, revert, restore, load, fetch }"
          :label-idle="isBusy ? 'Import in progress...' : 'Drop *.CSV file here...'"
          allow-multiple="true"
          accepted-file-types="text/plain"
          :disabled="isBusy"
          :files="uploadFiles"
          @init="handleFilePondInit"
          @updatefiles="handleFilePondAdd"
        />
      </div>
    </div>
    <div class="row mt-2">
      <div class="col">
        <span class="custom-message">
          <FontAwesomeIcon v-if="isImporting" icon="fa-solid fa-spinner me-2" spin />
          {{ message }}</span
        >
        <!-- Escape hatch: the progress feed can stall, and without this the form stays disabled until a reload. -->
        <button v-if="isImporting" class="btn btn-link btn-sm custom-unlock" @click="unlock">not responding? re-enable this form</button>
      </div>
    </div>
    <div class="row mt-2">
      <div class="col">
        <import-matrix-status ref="importStatus" @isComplete="complete" />
      </div>
    </div>
    <div class="row">
      <div class="col">
        <div class="row">
          <div class="col">
            <p>If you need to pull down existing category/vehicle matrix LINK data, use the following query to do so.</p>
          </div>
        </div>
        <div class="row">
          <div class="col">
            <div class="input-group">
              <span class="input-group-text">Type<span class="custom-required">*</span></span>
              <select v-model="lookupType" class="form-control" :disabled="isBusy" @change="changedMake">
                <option v-for="item of lookupTypes" :key="item.id" :value="item.id">{{ item.name }}</option>
              </select>
              <span class="input-group-text">Year<span class="custom-required">*</span></span>
              <select v-model="vehicleYear" class="form-control" :disabled="isBusy" @change="changedMake">
                <option v-for="item of viewYears" :key="item.id" :value="item.value">{{ item.name }}</option>
              </select>
              <span class="input-group-text">Make<span class="custom-required">*</span></span>
              <select v-model="vehicleMake" class="form-control" :disabled="isBusy" @change="changedMake">
                <option v-for="item of vehicles" :key="item.id" :value="item.value">{{ item.name }}</option>
              </select>
              <span id="basic-addon1" class="input-group-text">Model</span>
              <input
                v-model="vehicleModel"
                type="text"
                class="form-control"
                placeholder="Model"
                aria-label="Model"
                :disabled="isBusy"
                @keyup="changedMake"
              />
              <button class="btn btn-primary" :disabled="!isReady || isDownloading || isBusy" @click="fetchLinks">
                <FontAwesomeIcon v-if="isDownloading" icon="fa-solid fa-spinner me-2" spin />
                download
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
<script>
import { mapState, mapActions } from 'pinia'
import { authStore } from '@/stores/auth'
import { adasmatrixStore } from '@/stores/adasmatrix'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import moment from 'moment'
import ImportMatrixStatus from './matrix-import-status.vue'
import ZOverlay from '@/components/utilities/elements/Overlay.vue'
import axios from 'axios'
import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.css'
import { Parser } from '@json2csv/plainjs'
const FilePond = vueFilePond()
export default {
  name: 'MatrixImportLinks',
  components: {
    ImportMatrixStatus,
    ZOverlay,
    FilePond,
    FontAwesomeIcon
  },
  // Let the host lock its tab strip while this screen is busy.
  emits: ['busy'],
  watch: {
    isBusy(value) {
      this.$emit('busy', value)
    }
  },
  data() {
    return {
      firm: 1,
      isImporting: false,
      isUploading: false,
      uploadSource: null,
      uploadFiles: [],
      apiUrl: '',
      message: null,
      lookupTypes: [
        {
          id: 1,
          name: 'System',
          value: 1
        },
        {
          id: 2,
          name: 'Location',
          value: 2
        },
        {
          id: 3,
          name: 'Static',
          value: 3
        },
        {
          id: 4,
          name: 'Dynamic',
          value: 4
        }
      ],
      lookupType: null,
      vehicleYear: null,
      vehicleMake: null,
      vehicleModel: null,
      linkType: null,
      isReady: false,
      isDownloading: false
    }
  },
  mounted() {
    this.isReady = false
    this.isImporting = false
    this.isUploading = false
    this.isDownloading = false
    this.apiUrl = window.config ? window.config.apiUrl : ''
  },
  computed: {
    // Uploading or importing: either way the form must not accept another submission.
    isBusy() {
      return this.isUploading || this.isImporting
    },
    ...mapState(authStore, ['token']),
    ...mapState(adasmatrixStore, ['lookups', 'links']),
    viewYears() {
      let _data = [
        {
          id: 0,
          name: null,
          value: null
        }
      ]
      let cnt = 1
      for (let idx = new Date().getFullYear() + 3; idx >= 2007; idx--) {
        _data.push({
          id: cnt,
          name: idx,
          value: idx
        })
        cnt++
      }
      return _data
    },
    vehicles() {
      let _data = []
      _data.push({
        id: -9999,
        name: 'all',
        value: -9999
      })
      if (this.lookups['VehicleMake']) {
        this.lookups['VehicleMake'].forEach((element) => {
          _data.push(element)
        })
      }
      return _data
    }
  },
  methods: {
    ...mapActions(adasmatrixStore, ['importMakeMatrixLinks', 'fetchLinksByVehicle']),
    changedMake() {
      this.isReady = false
      if (this.lookupType) {
        if (this.vehicleYear) {
          if (this.vehicleMake) {
            this.isReady = true
          }
        }
      }
    },
    async fetchLinks() {
      let temp = this.vehicles.find((x) => x.id === this.vehicleMake)
      let make = null
      this.isDownloading = true
      if (temp) {
        make = temp.name
      }
      temp = this.lookupTypes.find((x) => x.id === this.lookupType)
      let system = null
      if (temp) {
        system = temp.name
      }
      let data = {
        year: this.vehicleYear,
        vehicleMake: this.vehicleMake,
        model: this.vehicleModel,
        type: this.lookupType
      }
      await this.fetchLinksByVehicle(data)
      console.log('Got Links', this.links)

      if (this.links) {
        var now = moment().format('MMDDYY')
        var fields = [
          'linkInfoId',
          'make',
          'model',
          'year',
          'adasCategory',
          'system',
          'location',
          'linkType',
          'linkGroup',
          'linkId',
          'linkName',
          'link',
          'exceptions'
        ]
        const json2csvParser = new Parser({ fields })
        const csv = json2csvParser.parse(this.links)
        // download the file as CSV
        var downloadLink = document.createElement('a')
        var blob = new Blob(['\ufeff', csv])
        var url = URL.createObjectURL(blob)
        downloadLink.href = url
        downloadLink.download = `ADAS Vehicle ${this.vehicleYear} ${make} ${this.vehicleModel ? this.vehicleModel : ''} ${system}Links ${now}.csv`
        document.body.appendChild(downloadLink)
        downloadLink.click()
        document.body.removeChild(downloadLink)
      }
      this.vehicleYear = null
      this.vehicleMake = null
      this.vehicleModel = null
      this.lookupType = null
      this.changedMake()
      this.isReady = false
      this.isDownloading = false
    },
    async importLinks(data) {
      let path = data.data.path
      let filename = data.data.filename
      this.isImporting = true
      console.log('Uploaded File', {
        path: path,
        filename: filename
      })
      this.$refs.pond.removeFiles({ revert: true })
      this.message = 'upload complete, processing...'
      await this.importMakeMatrixLinks({
        path: path,
        filename: filename
      })
    },
    complete() {
      this.isImporting = false
      this.message = 'import complete'
    },
    // Dismiss the upload overlay by cancelling the request behind it.
    cancelUpload() {
      if (this.uploadSource) {
        this.uploadSource.cancel('Operation canceled by the user.')
      }
      this.isUploading = false
      this.message = 'upload cancelled'
    },
    // Release the form when the progress feed has stopped reporting.
    unlock() {
      this.isImporting = false
      this.message = 'form re-enabled - the import may still be running on the server'
    },
    process(fieldName, file, metadata, load, error, progress) {
      let $this = this
      this.message = null
      this.isUploading = true
      // set data
      const formData = new FormData()
      formData.append('files', file, file.name)
      formData.append('firm', this.firm)
      formData.append('preference', 'MakeMatrixLinks')
      formData.append('path', `adasmatrix/${this.firm}`)
      // related to aborting the request
      const CancelToken = axios.CancelToken
      const source = CancelToken.source()
      this.uploadSource = source
      axios.interceptors.request.use(
        (config) => {
          config.headers['Authorization'] = 'Bearer ' + this.token
          return config
        },
        (error) => {
          return Promise.reject(error)
        }
      )
      // the request itself
      axios({
        method: 'post',
        url: `${this.apiUrl}/data/uploadFile`,
        data: formData,
        cancelToken: source.token,
        onUploadProgress: (e) => {
          // updating progress indicator
          progress(e.lengthComputable, e.loaded, e.total)
        }
      })
        .then((response) => {
          // Hand over from the blocking overlay to the disabled-form state.
          $this.isUploading = false
          $this.importLinks(response)
        })
        .catch((thrown) => {
          // Always clear the overlay, or a failed upload leaves the page blocked.
          $this.isUploading = false
          if (axios.isCancel(thrown)) {
            console.log('Request canceled', thrown.message)
          } else {
            console.log('Upload failed', thrown)
            $this.message = 'upload failed, nothing was imported - please try again'
          }
        })
      // Setup abort interface
      return {
        abort: () => {
          source.cancel('Operation canceled by the user.')
          $this.isUploading = false
        }
      }
    },
    handleError(error) {
      console.log('Error', error)
    },
    handleFilePondInit: function () {},
    handleFilePondAdd: function () {
      this.$refs.importStatus.reset()
    }
  }
}
</script>
<style lang="scss" scoped>
.custom-message {
  margin-left: 15px;
  margin-right: 15px;
  font-size: 0.9em;
}
.custom-export-wrapper {
  position: absolute;
  bottom: 20px;
}
.custom-required {
  color: red;
  position: relative;
  top: 0;
  right: 0;
}
.custom-unlock {
  font-size: 0.8em;
  padding: 0;
  vertical-align: baseline;
}
</style>
